import { useCallback, useEffect, useState } from 'react'
import { zendeskAPI } from 'src/config/routes'
import { fetchData } from 'src/utils'
import { useDebounce } from 'react-use'
import { SEARCH_DEBOUNCE_DELAY } from 'src/config/searchDebounceDelay'
import type { SearchResult, SearchResultItem } from '../types/entities'

type SearchArticlesResult = {
  isLoading: boolean
  searchResult: SearchResult | null
  error: Error | null
}

const useSearchFetch = (query: string, perPage?: number) => {
  const [loadingQueue, setLoadingQueue] = useState<Record<string, SearchArticlesResult['isLoading']>>({})
  const [searchResult, setSearchResult] = useState<SearchArticlesResult['searchResult']>(null)
  const [error, setError] = useState<SearchArticlesResult['error']>(null)

  useEffect(() => {
    setSearchResult(null)
    setError(null)
  }, [query])

  const fetch = useCallback(async (minQueryLength = 2) => {
    if (query.length > minQueryLength) {
      setLoadingQueue((prevState) => ({ ...prevState, [query]: true }))
      try {
        const response = await fetchData(zendeskAPI.articlesSearch(query, perPage))
        if (!response.results) {
          throw new Error('No results')
        }
        setSearchResult(response)
        setError(null)
      } catch (e) {
        setSearchResult(null)
        setError(e as Error)
      } finally {
        setLoadingQueue(({ [query]: _query, ...rest }) => rest)
      }
    }
  }, [perPage, query])

  return {
    fetch,
    loadingQueue,
    searchResult,
    error,
  }
}

const useSearchArticles = (query: string, perPage?: number): SearchArticlesResult => {
  const {
    fetch,
    loadingQueue,
    searchResult,
    error,
  } = useSearchFetch(query, perPage)

  useEffect(() => { fetch(0) }, [fetch, query])

  return {
    isLoading: Boolean(Object.keys(loadingQueue).length),
    searchResult,
    error,
  }
}

const useDebouncedSearchArticles = (query: string, perPage?: number): SearchArticlesResult => {
  const {
    fetch,
    loadingQueue,
    searchResult,
    error,
  } = useSearchFetch(query, perPage)

  useDebounce(fetch, SEARCH_DEBOUNCE_DELAY, [query])

  return {
    isLoading: Boolean(Object.keys(loadingQueue).length),
    searchResult,
    error,
  }
}

export { useSearchArticles, useDebouncedSearchArticles }
export type { SearchResultItem, SearchArticlesResult }
